import { Listing } from "src/types/listing";
import PropertiesList from "../neighborhoodSearch/components/PropertiesList";
import { PropertyDetails } from "../common/PropertyDetails";
import PropertyCardSkeleton from "../neighborhoodSearch/components/PropertyCardSkeleton";
import { useCallback } from "react";
import { useScrollBottomCheck } from "src/hooks/useScrollBottomCheck";
import NoListingsFound from "./NoListingsFound";

export type PropertiesListSidebarProps = {
  listings: Listing[];
  selectedListing: Listing | null | undefined;
  selectedListingId: string | null | undefined;
  onListingClick: (listing: Listing) => void;
  onListingImageClick?: (listing: Listing) => void;
  onAddNoteClick: () => void;
  onAddTagClick: () => void;
  onFavoriteClick?: (listing: Listing) => void;
  isLoadingSelectedListing: boolean;
  isLoadingListings: boolean;
  onScrollBottomReached?: () => void;
  onAdjustFiltersClick?: () => void;
  onClearFiltersClick?: () => void;
  isHideDNCPhoneNumbers?: boolean | null | undefined;
  isFavoritingEnabled?: boolean;
  // Details live in their own layer, so the list always stays visible
  suppressInlineDetails?: boolean;
};

export const PropertiesListSidebar = ({
  listings = [],
  selectedListing,
  selectedListingId,
  onListingClick,
  onListingImageClick,
  isLoadingSelectedListing,
  isLoadingListings,
  onAddNoteClick,
  onAddTagClick,
  onFavoriteClick,
  onScrollBottomReached,
  onAdjustFiltersClick,
  onClearFiltersClick,
  isHideDNCPhoneNumbers,
  isFavoritingEnabled = false,
  suppressInlineDetails = false,
}: PropertiesListSidebarProps) => {
  const handlePropertyDetailsFavoriteClick = useCallback(() => {
    selectedListing && onFavoriteClick?.(selectedListing);
  }, [selectedListing, onFavoriteClick]);

  const containerRef = useScrollBottomCheck(onScrollBottomReached);
  const showInlineDetails = !!selectedListingId && !suppressInlineDetails;

  return (
    <div className="h-full flex flex-col">
      {showInlineDetails && (
        <PropertyDetails
          isLoading={isLoadingSelectedListing || !selectedListing}
          listing={selectedListing || ({} as Listing)}
          isContactEditingDisabled
          isNotesEnabled
          isTagsEnabled
          onAddNoteClick={onAddNoteClick}
          onAddTagClick={onAddTagClick}
          onFavoriteClick={handlePropertyDetailsFavoriteClick}
          isHideDNCPhoneNumbers={isHideDNCPhoneNumbers}
        />
      )}

      {/* Keep the list mounted but hidden to preserve scroll position */}
      <div className={showInlineDetails ? "hidden" : "h-full flex flex-col"}>
        {isLoadingListings ? (
          <div className="flex flex-col gap-6">
            {Array.from({ length: 10 }).map((_, index) => (
              <PropertyCardSkeleton key={`skeleton-${index}`} />
            ))}
          </div>
        ) : listings.length === 0 ? (
          <NoListingsFound
            onAdjustFiltersClick={onAdjustFiltersClick}
            onClearFiltersClick={onClearFiltersClick}
          />
        ) : (
          <div ref={containerRef} className="flex-1 overflow-y-auto min-h-0">
            <PropertiesList
              listings={listings}
              selectedListingId={selectedListing?.id}
              onListingClick={onListingClick}
              onListingImageClick={onListingImageClick}
              onFavoriteClick={onFavoriteClick}
              isFavoritingEnabled={isFavoritingEnabled}
            />
          </div>
        )}
      </div>
    </div>
  );
};
